<template>
    <div class="elem-specs" dark-class="elem-specs-dark">
        <elem-input-object :name="name" :getValue="getValue"></elem-input-object>
        <div class="spec-group" v-for="(group, gIndex) in groups" :key="gIndex">
            <div class="group-header">
                <div class="label">规格种类：</div>
                <Input v-model="group.category" placeholder="如: 颜色、尺寸" style="width: 200px" />
                <div class="group-actions">
                    <Button type="text" icon="md-trash" class="btn-delete-group" @click="removeGroup(gIndex)">删除该组</Button>
                </div>
            </div>
            
            <div class="spec-table">
                <div class="table-header">
                    <div class="col title">名称</div>
                    <div class="col stock">库存</div>
                    <div class="col price">加价(元)</div>
                    <div class="col weight">加重量(克)</div>
                    <div class="col points">加积分</div>
                    <div class="col action">操作</div>
                </div>
                <div class="table-body">
                    <div class="table-row" v-for="(item, iIndex) in group.items" :key="iIndex">
                        <div class="col title">
                            <Input class="input" v-model="item.title" placeholder="如: 红色" />
                        </div>
                        <div class="col stock">
                            <Input class="input" v-model="item.stock" type="number" :min="0" placeholder="0" style="width: 100%" />
                        </div>
                        <div class="col price">
                            <Input class="input" v-model="item.price" type="number" :min="0" :step="0.01" placeholder="0.00" style="width: 100%" />
                        </div>
                        <div class="col weight">
                            <Input class="input" v-model="item.weight" type="number" :min="0" placeholder="0" style="width: 100%" />
                        </div>
                        <div class="col points">
                            <Input class="input" v-model="item.points" type="number" :min="0" placeholder="0" style="width: 100%" />
                        </div>
                        <div class="col action">
                            <Button type="text" icon="md-trash" class="btn-delete" @click="removeItem(gIndex, iIndex)" size="small"></Button>
                        </div>
                    </div>
                    <div class="table-footer">
                        <Button type="dashed" size="small" icon="md-add" @click="addItem(gIndex)">添加规格值</Button>
                    </div>
                </div>
            </div>
        </div>

        <div class="footer" v-if="groups.length > 0">
            <Button type="primary" ghost long icon="md-add" @click="addGroup">添加新规格组</Button>
        </div>
        
        <div class="empty" v-if="groups.length === 0" @click="addGroup">
            <Icon type="ios-apps-outline" size="52" class="empty-icon" />
            <div class="empty-text">暂无规格配置</div>
            <div class="empty-desc">点击此处添加第一个规格组，如“颜色”、“尺寸”等</div>
        </div>
    </div>
</template>

<script lang="ts">
import elemInputObject from "./elem-input-object.vue"
import { defineComponent, PropType, ref, watch } from 'vue';

interface Spec {
    uuid: string;
    category: string;
    title: string;
    price: number;
    weight: number;
    points: number;
    stock: number;
}

interface SpecItem {
    title: string;
    price: number;
    weight: number;
    points: number;
    stock: number;
    uuid?: string;
}

interface SpecGroup {
    category: string;
    items: SpecItem[];
}

export default defineComponent({
    components: {
        elemInputObject
    },
    name: 'ElemSpecs',
    props: {
        value: {
            type: Array as PropType<Spec[]>,
            default: () => []
        },
        name: String
    },
    emits: ['update:value', 'change'],
    setup(props, { emit }) {
        const groups = ref<SpecGroup[]>([]);
        let isInternalChange = false;

        // Convert flat list to groups
        const parseValue = (val: Spec[]) => {
            const newGroups: SpecGroup[] = [];
            // Use a map to preserve order of categories as they appear
            const map = new Map<string, SpecGroup>();
            
            val.forEach(item => {
                const cat = item.category || '';
                // If we haven't seen this category yet, create a new group
                if (!map.has(cat)) {
                    const group: SpecGroup = { category: cat, items: [] };
                    map.set(cat, group);
                    newGroups.push(group);
                }
                map.get(cat)!.items.push({
                    title: item.title,
                    price: item.price ? item.price / 100 : 0,
                    weight: item.weight || 0,
                    points: item.points,
                    stock: item.stock,
                    uuid: item.uuid
                });
            });
            return newGroups;
        };

        // Convert groups to flat list
        const flattenGroups = () => {
            const list: Spec[] = [];
            groups.value.forEach(g => {
                g.items.forEach(i => {
                    list.push({
                        category: g.category,
                        title: i.title,
                        price: i.price ? Math.round(i.price * 100) : 0,
                        weight: i.weight,
                        points: i.points,
                        stock: i.stock,
                        uuid: i.uuid
                    });
                });
            });
            return list;
        };

        watch(() => props.value, (val) => {
            if (isInternalChange) {
                isInternalChange = false;
                return;
            }
            
            if (val && Array.isArray(val)) {
                const currentFlat = flattenGroups();
                // Simple JSON comparison to avoid unnecessary re-renders/re-grouping
                if (JSON.stringify(currentFlat) !== JSON.stringify(val)) {
                    groups.value = parseValue(val);
                }
            } else {
                groups.value = [];
            }
        }, { immediate: true, deep: true });

        const emitChange = () => {
            const list = flattenGroups();
            isInternalChange = true;
            emit('update:value', list);
            emit('change', { name: props.name, value: list });
        };

        const addGroup = () => {
            groups.value.push({
                category: '',
                items: [{
                    title: '',
                    price: 0,
                    weight: 0,
                    points: 0,
                    stock: 0
                }]
            });
            // emitChange is triggered by watch(groups)
        };

        const removeGroup = (index: number) => {
            groups.value.splice(index, 1);
        };

        const addItem = (groupIndex: number) => {
            groups.value[groupIndex].items.push({
                title: '',
                price: 0,
                weight: 0,
                points: 0,
                stock: 0
            });
        };

        const removeItem = (groupIndex: number, itemIndex: number) => {
            groups.value[groupIndex].items.splice(itemIndex, 1);
        };

        // Deep watch to catch internal changes
        watch(groups, () => {
            emitChange();
        }, { deep: true });

        return {
            groups,
            addGroup,
            removeGroup,
            addItem,
            removeItem,
            emitChange,
            getValue: flattenGroups
        };
    }
});
</script>

<style lang="less" scoped>
@import (reference) "/src/style/utils.less";
@import (reference) "/src/style/color.less";

@primary-color: #2d8cf0;

.elem-specs {
    width: 100%;
    
    .spec-group {
        background: #fff;
        border: 1px solid #dcdee2;
        border-radius: 4px;
        margin-bottom: 15px;
        overflow: hidden;
        transition: all 0.2s ease-in-out;

        &:hover {
            box-shadow: 0 2px 12px 0 rgba(0,0,0,.1);
            border-color: #eee;
        }

        .group-header {
            display: flex;
            align-items: center;
            padding: 10px 15px;
            background: #f8f8f9;
            border-bottom: 1px solid #e8eaec;

            .label {
                font-size: 14px;
                font-weight: bold;
                color: #464c5b;
                margin-right: 10px;
            }

            .group-actions {
                margin-left: auto;
                .btn-delete-group {
                    color: #ed4014;
                    &:hover {
                        background: rgba(237, 64, 20, 0.1);
                    }
                }
            }
        }

        .spec-table {
            padding: 10px 15px;

            .table-header {
                display: flex;
                padding: 8px 0;
                border-bottom: 1px solid #e8eaec;
                font-size: 12px;
                color: #808695;
                
                .col {
                    padding: 0 5px;
                    text-align: center;
                }
            }

            .table-body {
                .table-row {
                    display: flex;
                    align-items: center;
                    padding: 10px 0;
                    border-bottom: 1px dashed #e8eaec;
                    
                    &:last-child {
                        border-bottom: none;
                    }

                    .col {
                        padding: 0 5px;
                    }
                }

                .table-footer {
                    padding-top: 10px;
                }
            }
        }
    }

    .col {
        &.title { flex: 1.5; }
        &.price { flex: 1; }
        &.weight { flex: 1; }
        &.points { flex: 1; }
        &.stock { flex: 1; }
        &.action { width: 50px; text-align: center; }
    }
    
    .btn-delete {
        color: #ed4014;
        &:hover {
            background-color: #fee;
        }
    }
    
    .footer {
        margin-top: 10px;
    }

    .empty {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 40px 20px;
        border: 1px dashed #dcdee2;
        border-radius: 4px;
        background: #fdfdfd;
        cursor: pointer;
        transition: all 0.3s;

        &:hover {
            border-color: @primary-color;
            background: #f8f8f9;
            
            .empty-icon, .empty-text {
                color: @primary-color;
            }
        }

        .empty-icon {
            color: #c5c8ce;
            margin-bottom: 10px;
            transition: color 0.3s;
        }

        .empty-text {
            font-size: 14px;
            font-weight: bold;
            color: #515a6e;
            margin-bottom: 5px;
            transition: color 0.3s;
        }

        .empty-desc {
            font-size: 12px;
            color: #808695;
        }
    }

    &.elem-specs-dark {
        .spec-group {
            background: #2b2e33;
            border-color: #444851;

            .group-header {
                background: #32353a;
                border-bottom-color: #444851;
            }

            .spec-table {
                .table-header {
                    border-bottom-color: #444851;
                    color: #b0b4be;
                }

                .table-body {
                    .table-row {
                        border-bottom-color: #444851;
                    }

                    .table-footer {
                        :deep(button) {
                            background: rgba(45, 140, 240, 0.1);
                            border-color: rgba(45, 140, 240, 0.2);
                            color: #2d8cf0;

                            span {
                                color: #2d8cf0;
                            }

                            &:hover {
                                background: rgba(45, 140, 240, 0.1);
                            }
                        }
                    }
                }
            }
        }

        .empty {
            border-color: #444851;
            background: #2b2e33;

            .empty-icon {
                color: #666a73;
            }

            .empty-text {
                color: #b0b4be;
            }
        }

        :deep(input) {
            background: #3a3d42;
            border-color: #55595f;
            color: #b0b4be;

            &::placeholder {
                color: #666a73;
            }
        }
    }
}
</style>
